import React, { useState } from 'react'
import Datepicker from '../DatePicker/Datepicker'
import CheckBoxFilter from '../../common/FilterComponent/CheckBoxFilter'
import ValidationBanner from '../ValidationBanner'
import defaultCheckboxFiltersConfig from '..//config/checkboxFilters.json'
import { isValidDateFormat, isPastDate } from '../../../utils/date'
import { scrollToTopAndFocusAlert } from '../../../utils/scrollUtils'

export interface FilterData {
  startDate: string
  endDate: string
  // map of filter key (from checkboxFilters.json) to selected label values
  checkboxFilters?: Record<string, string[]>
}

/** Shape of a single checkbox filter group. */
export interface CheckboxFilterGroupConfig {
  title: string
  labels: string[]
  disabledLabels?: string[]
}

interface FilterComponentProps {
  onApplyFilter: (filterData: FilterData) => void
  onCancel: () => void
  onReset?: () => void
  initialFilter?: FilterData
  dateMode?: 'all' | 'range'
  /**
   * Optional override for the checkbox filter options. When provided (e.g. from
   * the calendar-filters API), it replaces the bundled JSON defaults.
   */
  filtersConfig?: Record<string, CheckboxFilterGroupConfig>
    requiredFilters?: {
    key: string
    message: string
  }[]
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  onApplyFilter,
  onCancel,
  onReset,
  initialFilter,
  dateMode = 'range',
  filtersConfig,
  requiredFilters = [],
}) => {
  // Use API-provided options when available, otherwise the bundled JSON defaults
  const checkboxFiltersConfig = (filtersConfig ??
    defaultCheckboxFiltersConfig) as Record<string, CheckboxFilterGroupConfig>

  const [startDate, setStartDate] = useState<string>(
    initialFilter?.startDate || ''
  )
  const [endDate, setEndDate] = useState<string>(initialFilter?.endDate || '')
  const [showRequiredError, setShowRequiredError] = useState<boolean>(false)
  const [showDateRangeError, setShowDateRangeError] = useState<boolean>(false)
  const [checkboxValidationErrors, setCheckboxValidationErrors] = useState< Record<string, string>>({})
  const [isEndDateModified, setIsEndDateModified] = useState(
    Boolean(initialFilter?.endDate)
  )
  const startDateError =
    startDate && !isValidDateFormat(startDate) ? 'Invalid date format' : ''
  const endDateError =
    endDate && !isValidDateFormat(endDate) ? 'Invalid date format' : ''

  const startDateOutOfRangeError =
    startDate && isValidDateFormat(startDate) && isPastDate(startDate)
      ? 'This value is out of range'
      : ''
  const endDateOutOfRangeError =
    endDate && isValidDateFormat(endDate) && isPastDate(endDate)
      ? 'This value is out of range'
      : ''

  // Helper function to convert MM/DD/YYYY to Date object
  const parseDate = React.useCallback((dateStr: string): Date | null => {
    const parts = dateStr.split('/')
    if (parts.length !== 3) return null
    const [mm, dd, yyyy] = parts
    return new Date(
      Number.parseInt(yyyy),
      Number.parseInt(mm) - 1,
      Number.parseInt(dd)
    )
  }, [])

  // Required field validation (only for date range mode)
  const requiredError = React.useMemo(() => {
    if (!showRequiredError || dateMode !== 'range') return ''
    if (!startDate || !endDate) {
      return 'Start Date and End Date are required'
    }
    return ''
  }, [showRequiredError, startDate, endDate, dateMode])

  // Date range validation: end date cannot be earlier than start date
  const dateRangeError = React.useMemo(() => {
    if (!showDateRangeError) return ''
    if (!startDate || !endDate || startDateError || endDateError) return ''

    const startDateObj = parseDate(startDate)
    const endDateObj = parseDate(endDate)

    if (!startDateObj || !endDateObj) return ''

    if (endDateObj < startDateObj) {
      return 'End date must be greater than start date.'
    }

    return ''
  }, [
    showDateRangeError,
    startDate,
    endDate,
    startDateError,
    endDateError,
    parseDate,
  ])

  // Initialize selection map for each filter group from config
  // statusFilter and requestTypeFilter are checked by default
  const getInitialCheckboxSelections = () => {
    const selections = Object.fromEntries(
      Object.keys(checkboxFiltersConfig).map((key) => {
        // Pre-select all items for statusFilter and requestTypeFilter
        if (key === 'statusFilter' || key === 'requestTypeFilter') {
          const config = checkboxFiltersConfig[key]
          const allSelected = Object.fromEntries(
            (config?.labels || []).map((label) => [label, true])
          )
          return [key, allSelected]
        }
        return [key, {}]
      })
    ) as Record<string, Record<string, boolean>>

    // If initialFilter has checkbox selections, restore them (overrides defaults)
    if (initialFilter?.checkboxFilters) {
      for (const filterKey in initialFilter.checkboxFilters) {
        const selectedLabels = initialFilter.checkboxFilters[filterKey]
        if (Array.isArray(selectedLabels)) {
          selections[filterKey] = Object.fromEntries(
            selectedLabels.map((label) => [label, true])
          )
        }
      }
    }
    return selections
  }

  // Get default selections (status and request type pre-selected, others empty)
  const getDefaultCheckboxSelections = () => {
    return Object.fromEntries(
      Object.keys(checkboxFiltersConfig).map((key) => {
        // Pre-select all items for statusFilter and requestTypeFilter
        if (key === 'statusFilter' || key === 'requestTypeFilter') {
          const config = checkboxFiltersConfig[key]
          const allSelected = Object.fromEntries(
            (config?.labels || []).map((label) => [label, true])
          )
          return [key, allSelected]
        }
        return [key, {}]
      })
    ) as Record<string, Record<string, boolean>>
  }
React.useEffect(() => {
  if (dateMode === 'all') {
    setStartDate('')
    setEndDate('')
    setIsEndDateModified(false)
    setShowRequiredError(false)
    setShowDateRangeError(false)
    setCheckboxValidationErrors({})
  }
}, [dateMode])
  const initialCheckboxSelections = getInitialCheckboxSelections()
  const [checkboxSelections, setCheckboxSelections] = useState<
    Record<string, Record<string, boolean>>
  >(initialCheckboxSelections)

  const handleApplyFilter = () => {
    // Check for required fields first (only when date range is selected)
    if (dateMode === 'range') {
      if (!startDate || !endDate) {
      setShowRequiredError(true)
      setShowDateRangeError(false)
      scrollToTopAndFocusAlert()
      return
    }

    // Clear required error if dates are filled or mode is 'all'
    setShowRequiredError(false)

    // Validate date formats before submitting — invalid format blocks apply
    if (startDateError) {
      document
        .querySelector('[name="filterStartDate"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (endDateError) {
      document
        .querySelector('[name="filterEndDate"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
  }

    // Check date range validation
    const startDateObj = parseDate(startDate)
    const endDateObj = parseDate(endDate)

    if (startDateObj && endDateObj && endDateObj < startDateObj) {
      setShowDateRangeError(true)
      scrollToTopAndFocusAlert()
      return
    }

    // Clear all errors if validation passes
    setShowDateRangeError(false)

    // Convert selected checkboxes map to array of selected labels per filter
    const selectedCheckboxFilters: Record<string, string[]> = {}
    for (const filterKey in checkboxSelections) {
      const selections = checkboxSelections[filterKey] || {}
      selectedCheckboxFilters[filterKey] = Object.keys(selections).filter(
        (label) => selections[label]
      )
    }
const checkboxErrors: Record<string, string> = {}

for (const required of requiredFilters) {
  const selected = selectedCheckboxFilters[required.key] ?? []

  const validSelected = selected.filter(
    (item) => !/^all(\s|$)/i.test(item)
  )

  if (validSelected.length === 0) {
    checkboxErrors[required.key] = required.message
  }
}

if (Object.keys(checkboxErrors).length > 0) {
  setCheckboxValidationErrors(checkboxErrors)
  scrollToTopAndFocusAlert()
  return
}

setCheckboxValidationErrors({})
    const filterData: FilterData = {
      startDate,
      endDate,
      checkboxFilters: selectedCheckboxFilters,
    }
    // eslint-disable-next-line no-console
    onApplyFilter(filterData)
  }

  const handleResetToAll = () => {
    setCheckboxSelections(getDefaultCheckboxSelections())
    setStartDate('')
    setEndDate('')
    setIsEndDateModified(false)
    onReset?.()
  }

  const handleCheckboxSelection = (
    filterKey: string,
    selections: Record<string, boolean>
  ) => {
    setCheckboxSelections((prev) => ({ ...prev, [filterKey]: selections }))
      // Clear checkbox validation error once user changes selection
setCheckboxValidationErrors((prev) => {
  const updated = { ...prev }
  delete updated[filterKey]
  return updated
})
  }

  return (
    <div>
      {/* Content */}
      {/*Banner */}
      {Object.keys(checkboxValidationErrors).length > 0 && (
        <ValidationBanner
          variant="error"
          heading="Error"
          message={
            <>
              {Object.values(checkboxValidationErrors).map((error) => (
                <React.Fragment key={error}>
                  {error}
                  <br />
                </React.Fragment>
              ))}
            </>
          }
        />
      )}
      {/* Required Fields Error Banner */}
      {requiredError && (
        <div className="mb-6">
          <ValidationBanner
            variant="error"
            heading="Error"
            message={requiredError}
          />
        </div>
      )}

      {/* Date Range Error Banner */}
      {!requiredError && dateRangeError && (
        <div className="mb-6">
          <ValidationBanner
            variant="error"
            heading="Error"
            message={dateRangeError}
          />
        </div>
      )}

      {/* Date Inputs */}
      <div className="flex flex-col md:flex-row flex-wrap gap-4 md:gap-8 mb-6 md:mb-8">
        {/* Start Date */}
        <div className="w-full md:w-[20rem]!">
          <Datepicker
            name="filterStartDate"
            placeholder=""
            label="Start Date"
            openBehaviour="iconView"
            helperText="MM/DD/YYYY"
            value={startDate}
            disabled={dateMode === 'all'}
            onChange={(_name, value) => {
              const newStartDate = Array.isArray(value) ? value[0] : value || ''

              setStartDate(newStartDate)

              // Only auto-populate if user hasn't modified End Date
              if (!isEndDateModified && !endDate) {
                setEndDate(newStartDate)
              }
            }}
          />
          {dateMode === 'range' && startDateError && (
            <p className="p4 -book text-red-600 text-sm mt-1">
              {startDateError}
            </p>
          )}
          {dateMode === 'range' && !startDateError && startDateOutOfRangeError && (
            <p className="p4 -book text-red-600 text-sm mt-1">
              {startDateOutOfRangeError}
            </p>
          )}
        </div>

        {/* End Date */}
        <div className="md:w-[20rem]!">
          <Datepicker
            name="filterEndDate"
            placeholder=""
            label="End Date"
            openBehaviour="iconView"
            helperText="MM/DD/YYYY"
            value={endDate}
            disabled={dateMode === 'all'}
            onChange={(_name, value) => {
              const newEndDate = Array.isArray(value) ? value[0] : value || ''
              setEndDate(newEndDate)
              setIsEndDateModified(true)
            }}
          />
          {dateMode === 'range' && endDateError  && (
            <p className="p4 -book text-red-600 text-sm mt-1">{endDateError}</p>
          )}
          {dateMode === 'range' && !endDateError && endDateOutOfRangeError && (
            <p className="p4 -book text-red-600 text-sm mt-1">
              {endDateOutOfRangeError}
            </p>
          )}
        </div>
      </div>
      <div className="w-full min-w-0">
     <div className="w-full flex-1 min-w-0">
  <p className="p4 -dolphin text-xs sm:text-sm w-[20rem]!">
    (Will include requests that begin or end outside this range)
  </p>
</div>
</div>

      {/* Checkbox Filters - 1 column on mobile, 2 columns on desktop */}
      <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-2 gap-y-2 sm:gap-y-3 gap-x-0 sm:gap-x-3 md:gap-x-4 items-start">
        {Object.entries(checkboxFiltersConfig)
          .filter(
            ([key]: [string, any]) =>
              key !== 'statusFilter' && key !== 'requestTypeFilter'
          )
          .map(([key, config]: [string, any]) => (
            <div key={key} className="mt-6 w-[20rem] md:w-full min-w-0 overflow-hidden">
              <CheckBoxFilter
                title={config.title}
                labelsArray={config.labels}
                onSelectionChange={(selections: Record<string, boolean>) =>
                  handleCheckboxSelection(key, selections)
                }
                initialSelections={checkboxSelections[key]}
              />
            </div>
          ))}

        {/* Request Status and Type - below first filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-1 w-full min-w-0">
          {['statusFilter', 'requestTypeFilter'].map((key: string) => {
            const config = (checkboxFiltersConfig as any)[key]
            if (!config) return null
            return (
              <div key={key} className="w-full min-w-0">
                <CheckBoxFilter
                  title={config.title}
                  labelsArray={config.labels}
                  onSelectionChange={(selections: Record<string, boolean>) =>
                    handleCheckboxSelection(key, selections)
                  }
                  initialSelections={checkboxSelections[key]}
                  noBg="true"
                  disabledLabels={config.disabledLabels}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col md:flex-row gap-3 mt-8! w-full">
        <button
          className="w-[18rem]! md:w-auto px-6 py-2 button -secondary"
          onClick={onCancel}
        >
          Cancel
        </button>

        <button
          className="w-[18rem]! md:w-auto px-6 py-2 button -secondary"
          onClick={handleResetToAll}
        >
          Default
        </button>

        <button
          className="w-[18rem]! md:w-auto px-6 py-2 button -primary -light"
          onClick={handleApplyFilter}
        >
          Apply Filter
        </button>
      </div>
    </div>
  )
}

export default FilterComponent
